import { Box, Stack, useTheme } from '@mui/material';
import {
  AddCircleOutline,
  Agriculture,
  CalendarMonth,
  Chat,
  LocalMall,
  NotificationsActiveOutlined,
  PeopleAltOutlined,
  Place,
  Receipt,
  ShoppingBasket,
  ShoppingCart,
  Storefront,
  Work,
} from '@mui/icons-material';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useMuiSwitchableTheme } from '@farmshare/ui-components';
import {
  EnumUserRelationshipsType,
  useNavBadgeCountsQuery,
  useProducerActionCountsQuery,
} from '../../../lib/graphql';
import { userState } from 'state';
import { useCognitoAuth } from 'features/auth';
import { useProducerQuickSchedule } from 'features/jobs/hooks/use-producer-quick-schedule';
import { useAuthStorageCleanup } from 'lib/hooks/use-auth-storage-cleanup';
import { isBuyerOnly } from 'lib/userHelpers';
import { PortalLiveToasts } from '../shared/portal-live-toasts';

import { useProducerNav } from '../nav/hooks/use-producer-nav';
import {
  buildProducerNavConfig,
  ProducerNavModule,
} from '../nav/producer-nav-config';
import {
  useWaitlistOpenings,
  WaitlistOpeningsContext,
} from '../producer/producer-dashboard/use-waitlist-openings';
import { IconRail } from '../nav/icon-rail';
import { SecNavPanel } from '../nav/sec-nav-panel';
import { MobileTopBar } from '../nav/mobile-top-bar';
import { MobileDrawer } from '../nav/mobile-drawer';

interface ProducerShellProps {
  children: ReactNode;
}

const FULLSCREEN_ROUTE_PREFIXES = [
  '/scheduling',
  '/my-processor',
  // Full-bleed marketplace: the buy half's full-width sticky species bar, the
  // process half's edge-to-edge map, and the results page's side-by-side list +
  // map all use the full panel width (no shell max-width / top-and-side
  // padding). Covers /marketplace and children. `/explore`, `/buyer/find`, and
  // `/find-a-processor` are pure redirects into it now, so they need no entry.
  '/marketplace',
];

// Routes that keep their desktop framing but run edge-to-edge on phones.
// Unlike FULLSCREEN_ROUTE_PREFIXES this is breakpoint-scoped: a full-height
// list page (SearchPage and friends) wants the last 12px a side of a ~390px
// screen, and the 24px the shell puts above it is a row of the list — but its
// desktop layout, max-width band and all, is fine as it is.
//
// It also squares the height maths. SearchPage sizes itself
// `100vh - marginTop`, where `marginTop` is the top bar alone; any padding the
// shell adds above it is height the container has already spent, so the table
// overhangs the viewport by exactly that much and scrolls inside itself.
const MOBILE_FULL_BLEED_ROUTE_PREFIXES = [
  '/customers',
  '/jobs',
  // `/job/:id` (singular) too, now that it runs on DetailPageLayout. That
  // chassis is full-bleed on phones by design and sizes its column
  // `100dvh - app header`, so the shell's gutter both inset a page meant to
  // reach the edges and spent 24px of a height the column had already
  // claimed — the page scrolled to reveal a column that scrolled again.
  // Trailing slash so it can't also swallow some future `/jobfoo`.
  '/job/',
  // The buyer's cut sheets for one job, on the same chassis for the same
  // reason. Trailing slash, so the `/buyer/orders` redirect is untouched.
  '/buyer/orders/',
  // My Animals moved onto the same SearchPage chassis as /customers, so it
  // needs the same treatment: the shell's 24px above and 12px below were a
  // band of empty page over the search bar, and the chassis had already
  // counted that height against the viewport.
  '/my-animals',
  '/chat',
  // A processor's storefront. On a phone its hero and sections run to the
  // screen edges like /my-processor's (which is fullscreen outright); the page
  // supplies the gutter for its text rows and its own top/bottom air. Applies
  // to buyer-only users too (BuyerShell renders this shell), and the page
  // assumes it for every shell user. Both URL forms, trailing slash so
  // `/processors` alone is untouched.
  '/processors/',
  '/buyer/processor/',
  // The dashboard's tab strip is sticky and has to sit FLUSH under the top
  // bar. Any padding the shell adds above it is a band the strip can never
  // close — and trying to cancel it from the page with a negative margin
  // means the two have to agree on a number forever. Full bleed makes the
  // content top exactly the bar height, which is the same number the strip
  // sticks at, so they cannot drift. The page supplies its own side gutter.
  '/dashboard',
];

function ProducerShellInner({ children }: ProducerShellProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { pathname } = useLocation();
  const user = useRecoilValue(userState);
  const { logout } = useCognitoAuth();
  const { markLogout } = useAuthStorageCleanup();

  const isFullscreen = FULLSCREEN_ROUTE_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );
  const isMobileFullBleed =
    isFullscreen ||
    MOBILE_FULL_BLEED_ROUTE_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    );

  const handleLogout = useCallback(async () => {
    markLogout();
    await logout();
    navigate('/login');
  }, [logout, markLogout, navigate]);

  const buyerOnly = user ? isBuyerOnly(user) : false;

  const hasProcessors = useMemo(
    () =>
      (user?.relationships ?? []).some(
        (r) =>
          r?.type === EnumUserRelationshipsType.Producer &&
          Boolean(r?.vendorId),
      ),
    [user?.relationships],
  );

  // Same round-trip + polling cadence the processor top-nav badge uses
  // (see app.tsx) — `unreadChats` already covers this user's direct AND
  // processor rooms (buildChatRoomAccessMatch's participant scoping), so the
  // portal rail Chat badge reuses it rather than counting again.
  const { data: navBadgeData } = useNavBadgeCountsQuery({
    skip: !user,
    fetchPolicy: 'cache-and-network',
    pollInterval: 15_000,
  });
  const chatUnreadCount = navBadgeData?.navBadgeCounts?.unreadChats ?? 0;

  // Every outstanding item, summed — not the number of KINDS of outstanding
  // item. Seven deposits and thirty-three cut sheets is forty things a
  // producer owes someone, and a badge reading "2" would understate it to
  // the point of being useless. The Tasks tab badge uses the same total, so
  // the nav and the page can't disagree. A buyer's Tasks page carries only
  // their cut sheets and the invoices billed to them, so their badge is those
  // two alone.
  const { data: actionCountsData } = useProducerActionCountsQuery({
    skip: !user,
    variables: { buyerOnly },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });
  const counts = actionCountsData?.producerActionCounts;
  // The one Tasks row the server's counts don't cover; added here so the
  // nav badge and the page's total stay the same number. Counted once, here,
  // and provided to the Tasks tab rather than counted again there.
  const waitlistOpenings = useWaitlistOpenings(!user || buyerOnly);
  const taskCount = !counts
    ? 0
    : buyerOnly
      ? counts.missingCutsheets + counts.openInvoices
      : counts.depositDue +
        counts.dropoffsToConfirm +
        counts.missingSplits +
        counts.missingBuyers +
        counts.missingCutsheets +
        counts.openInvoices +
        waitlistOpenings;

  const modules = useMemo(
    () =>
      buildProducerNavConfig(
        {
          requestJob: <AddCircleOutline />,
          dashboard: <NotificationsActiveOutlined />,
          jobs: <Work />,
          animals: <Agriculture />,
          chat: <Chat />,
          orders: <ShoppingBasket />,
          processors: <Storefront />,
          scheduleJob: <CalendarMonth />,
          find: <Place />,
          available: <LocalMall />,
          transactions: <Receipt />,
          // There is no cart nav item (the cart was removed with the
          // marketplace, PRO-784); the icon only satisfies the config's type.
          cart: <ShoppingCart />,
          customers: <PeopleAltOutlined />,
        },
        { buyerOnly, hasProcessors, chatUnreadCount, taskCount },
      ),
    [buyerOnly, hasProcessors, chatUnreadCount, taskCount],
  );

  const nav = useProducerNav(modules);

  // Sync producer nav width with theme breakpoints.
  // IconRail is 64px (collapsed left nav); on mobile there's no sidebar.
  const {
    setNavMode,
    setNavCollapsed,
    isMobile: isPhone,
  } = useMuiSwitchableTheme();
  useEffect(() => {
    if (nav.isMobile) {
      setNavMode('top');
      setNavCollapsed(true);
    } else {
      setNavMode('left');
      setNavCollapsed(true);
    }
  }, [nav.isMobile, setNavMode, setNavCollapsed]);

  const visibleModuleData: ProducerNavModule | undefined = useMemo(
    () => modules.find((m) => m.id === nav.visibleModule),
    [modules, nav.visibleModule],
  );

  const { open: openQuickSchedule } = useProducerQuickSchedule();

  const handleClickModule = (mod: ProducerNavModule) => {
    if (mod.action === 'callback') {
      if (mod.id === 'requestJob') openQuickSchedule();
      return;
    }
    if (mod.href) {
      navigate(mod.href);
    }
  };

  const mobileGutter = theme.spacing(isMobileFullBleed ? 0 : 1.5);

  return (
    <WaitlistOpeningsContext.Provider value={buyerOnly ? 0 : waitlistOpenings}>
      <Stack
        direction="row"
        sx={{
          // `dvh`, not `vh`. On iOS `100vh` is the height with Safari's toolbar
          // tucked away, so the shell stood taller than the screen: the page
          // itself scrolled by the toolbar's height, carrying a full-height
          // page's header (chat, most visibly) off the top and leaving a band
          // of nothing under it. `dvh` is the height actually on screen.
          //
          // `vh` stays as the fallback: a browser without `dvh` (iOS before
          // 15.4) drops the declaration, and a shell with no height at all
          // stops the content area below from being the scroller.
          //
          // On a phone the shell is only AT LEAST that tall, and the content
          // area below does not scroll: the document does. That is the one kind
          // of scroll that makes the browser tuck its own toolbar away, which
          // on a phone in landscape is a sixth of the screen. The top bar is
          // fixed, so it stays put either way.
          ...(isPhone
            ? {
                minHeight: '100vh',
                '@supports (height: 100dvh)': { minHeight: '100dvh' },
              }
            : {
                height: '100vh',
                '@supports (height: 100dvh)': { height: '100dvh' },
              }),
          p: { xs: 0, md: 1.5 },
          bgcolor: 'primary.main',
        }}
      >
        {/* Desktop nav */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <IconRail
            modules={modules}
            activeModule={nav.activeModule}
            hoveredModule={nav.hoveredModule}
            isSecNavVisible={nav.isSecNavVisible}
            onHoverModule={nav.setHoveredModule}
            onClickModule={handleClickModule}
            userName={user?.first_name || undefined}
            onLogout={handleLogout}
          />
          <SecNavPanel
            module={visibleModuleData}
            isVisible={nav.isSecNavVisible}
            isHovered={nav.hoveredModule !== null}
            isPinned={nav.isPinned}
            onTogglePin={nav.togglePin}
            onHover={nav.setHoveredModule}
          />
        </Box>

        {/* Mobile nav */}
        <MobileTopBar
          onOpenDrawer={nav.toggleMobileDrawer}
          height={nav.MOBILE_TOP_BAR_HEIGHT}
          // Everything the drawer is hiding: tasks plus unread chats. On a
          // phone the nav IS this button, so the badge is the only signal
          // that something behind it wants attention.
          badgeCount={taskCount + chatUnreadCount}
        />
        <MobileDrawer
          open={nav.isMobileDrawerOpen}
          onClose={nav.closeMobileDrawer}
          modules={modules}
          activeModule={nav.activeModule}
          onClickModule={handleClickModule}
        />

        {/* Content area */}
        <Box
          component="main"
          sx={{
            bgcolor: 'background.default',
            flexGrow: 1,
            minWidth: 0,
            overflow: isPhone ? 'visible' : 'auto',
            // Where a bar inside a page pins. This box's content edge while it
            // is the scroller; under the fixed top bar once the document is.
            '--page-sticky-top': isPhone
              ? 'var(--app-header-height, 0px)'
              : '0px',
            pt: {
              xs: `${nav.MOBILE_TOP_BAR_HEIGHT + (isMobileFullBleed ? 0 : 24)}px`,
              // 2, not 4. On desktop the pages inside this already carry their
              // own top padding, so 32px here was a second gap above every
              // page header — height the full-height list pages spend on
              // nothing rather than on rows.
              md: isFullscreen ? 0 : 2,
            },
            // The xs insets clear the notch on a phone in landscape (either
            // side, depending on rotation). Padding, so the page colour still
            // fills the notch area — full-bleed routes included.
            pl: {
              xs: `calc(${mobileGutter} + env(safe-area-inset-left, 0px))`,
              md: isFullscreen ? 0 : 3,
            },
            pr: {
              xs: `calc(${mobileGutter} + env(safe-area-inset-right, 0px))`,
              md: isFullscreen ? 0 : 3,
            },
            pb: { xs: isMobileFullBleed ? 0 : 1.5, md: isFullscreen ? 0 : 3 },
            transition: 'margin-left 0.3s ease',
            ml: { xs: 0, md: 1.5 },
            borderRadius: { xs: 0, md: 2 },
            '@media print': {
              pt: 0,
            },
          }}
        >
          {isFullscreen ? (
            children
          ) : (
            <Box sx={{ maxWidth: { md: 1200 }, mx: { md: 'auto' } }}>
              {children}
            </Box>
          )}
        </Box>

        {/* Renders nothing; announces new notifications and chat messages as
          toasts. At shell level so it keeps working wherever you navigate. */}
        <PortalLiveToasts />
      </Stack>
    </WaitlistOpeningsContext.Provider>
  );
}

export function ProducerShell({ children }: ProducerShellProps) {
  return <ProducerShellInner>{children}</ProducerShellInner>;
}
